import { AppId, FCX, getEnding, getFormattedThousand, ProductLogo, TextInput } from "@datassential/platform-ui-lib";
import FoodSection from "@pages/Food/components/Section/Section";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import FoodTabs from "@pages/Food/components/Tabs/Tabs";
import useFoodLaunches, { useFoodLaunchesWithSearch } from "@apiHooks/useFoodLaunches";
import Grid from "./Grid";
import Summary from "./Summary";
import PermissionOverlay from "@pages/Food/components/PermissionOverlay/PermissionOverlay";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { requestTrialByFeature } from "@actions/page";
import { PermissionLevelEnum } from "@models/PermissionSection";
import { iLTO } from "@models/LTO";
import cn from "classnames";
import { FoodSectionEnum } from "@pages/Food/Food.constants";
import { AiOutlineSearch } from 'react-icons/ai';
import './SectionLaunches.scss';

enum LaunchesTabs {
    Items = 'Items',
    Summary = 'Summary',
}

/** The locked preview shows two blurred rows of cards (Figma), never the full list. */
const LOCKED_PREVIEW_MAX_ITEMS = 6;

/**
 * Launches section, shared by both Food Profile versions.
 * A page version can compose its own header around the Items / Summary tabs (`renderHeader`)
 * and add an action next to the search box (`searchAction`); without them the section renders as on the legacy page.
 * `lockedPreview` opts into a locked preview for users without access: the section (and its nav item) shows even when
 * there are no items, the grid falls back to `lockedPreview.items` when the backend returned none, and the overlay
 * shows `renderMessage(itemsCount)` in a centred box instead of the legacy upgrade copy.
 */
const FoodSectionLaunches: FCX<{
    word: string;
    setHasData: (hasData: boolean) => void;
    renderHeader?: (tabs: ReactNode) => ReactNode;
    searchAction?: ReactNode;
    lockedPreview?: {
        items: iLTO[];
        renderMessage: (itemsCount: number) => ReactNode;
    };
}> = ({
    word,
    setHasData,
    renderHeader,
    searchAction,
    lockedPreview,
}) => {
    const dispatch = useDispatch();
    const requestTrial: (f: string, hideNotification?: boolean) => any = (featureName, hideNotification) => {
        if (!hideNotification) {
            toast.info('Request has been sent');
        }
        return dispatch(requestTrialByFeature(featureName));
    };
    const [secondaryQuery, setSecondaryQuery] = useState<string>('');
    const [submittedQuery, setSubmittedQuery] = useState<string>('');

    const onSecondarySearchSubmit = useCallback(() => {
        setSubmittedQuery(secondaryQuery);
    }, [secondaryQuery]);

    useEffect(() => {
        if (secondaryQuery.trim() === '') {
            setSubmittedQuery('');
        }
    }, [secondaryQuery]);

    const {
        isLoading: isMainLoading,
        data: {
            itemsCount: mainItemsCount,
            summary: mainSummary,
            ltos: mainLtos,
        },
    } = useFoodLaunches(word);

    const {
        isLoading: isSecondaryLoading,
        data: {
            itemsCount: secondaryItemsCount,
            summary: secondarySummary,
            ltos: secondaryLtos,
        },
    } = useFoodLaunchesWithSearch(word, submittedQuery);

    const isSecondaryQueryApplied = submittedQuery.trim() !== '';

    const isLoading = isMainLoading || isSecondaryLoading;
    const ltos = useMemo(
        () => isSecondaryQueryApplied ? secondaryLtos : mainLtos,
        [isSecondaryQueryApplied, secondaryLtos, mainLtos]
    );
    const summary = useMemo(
        () => isSecondaryQueryApplied ? secondarySummary : mainSummary,
        [isSecondaryQueryApplied, secondarySummary, mainSummary]
    );
    const itemsCount = useMemo(
        () => isSecondaryQueryApplied ? secondaryItemsCount : mainItemsCount,
        [isSecondaryQueryApplied, secondaryItemsCount, mainItemsCount]
    );

    const [selectedTab, setSelectedTab] = useState<LaunchesTabs>(LaunchesTabs.Items);

    const isLocked = !!lockedPreview;
    const hasFullAccess = !isLocked && ltos.permissionLevel === PermissionLevelEnum.Full;
    // Locked: always the fake placeholder items, never BE records (they would still be readable in the DOM behind the blur).
    const gridData = isLocked ? (lockedPreview?.items ?? []).slice(0, LOCKED_PREVIEW_MAX_ITEMS) : ltos.data;
    const showItemsCount = !isLoading && !(isLocked && itemsCount === 0);

    useEffect(() => {
        setHasData(isLocked || mainItemsCount > 0);
    }, [isLocked, mainItemsCount, setHasData]);

    if (isMainLoading) {
        return (
            <div className="Food__section is-loading">
                <div className="spinner spinner-blink">
                    <ProductLogo appId={AppId.Snap} type="regular"/>
                </div>
            </div>
        );
    }

    if (mainItemsCount === 0 && !isLocked) return null;

    const tabs = (
        <FoodTabs
            modifiers={['height-l']}
            data={[
                { id: LaunchesTabs.Items, title: LaunchesTabs.Items },
                { id: LaunchesTabs.Summary, title: LaunchesTabs.Summary },
            ]}
            activeTabId={selectedTab}
            onItemClick={(id) => setSelectedTab(id as LaunchesTabs)}
            testId="blockFoodSectionLaunchesTabs"
        />
    );

    return (
        <div
            className="Food__section"
            id={FoodSectionEnum.Launches}
            data-testid="blockFoodSectionLaunches"
        >
            <div className={cn(
                "FoodSectionLaunches",
                !isLocked && !hasFullAccess && 'p-bottom'
            )}>
                <div className="FoodSectionLaunches__header">
                    {renderHeader ? renderHeader(tabs) : (
                        <FoodSection
                            headerContent={(<>
                                <div data-testid="textFoodLaunchesTitle">
                                    LAUNCHES
                                </div>
                                {tabs}
                            </>)}
                        />
                    )}
                </div>
                <div className="FoodSectionLaunches__sub-header">
                    {/* No search in the locked preview: there is nothing the user can search through. */}
                    {!isLocked && (
                        <div className="FoodSectionLaunches__search-box">
                            <TextInput
                                value={secondaryQuery}
                                setValue={isSecondaryLoading ? () => undefined : setSecondaryQuery}
                                onSubmit={isSecondaryLoading ? undefined : onSecondarySearchSubmit}
                                placeholder="Add to your search..."
                                className="FoodSectionLaunches__search"
                                isDisabled={isSecondaryLoading && submittedQuery.trim() !== ''}
                                testId="inputFoodSectionLaunchesSearch"
                            />
                            <AiOutlineSearch
                                className="FoodSectionLaunches__search-icon"
                                onClick={isSecondaryLoading ? undefined : onSecondarySearchSubmit}
                                data-testid="buttonFoodSectionLaunchesSearchIcon"
                            />
                        </div>
                    )}
                    {searchAction}
                    {showItemsCount && (
                        <div
                            className="FoodSectionLaunches__count"
                            data-testid="textFoodLaunchesSubHeader"
                        >
                            Chains featured
                            <b data-testid="textFoodLaunchesSubHeaderResultsCount"> {getFormattedThousand(itemsCount)} </b>
                            new {getEnding('item', itemsCount)} with
                            <b data-testid="textFoodLaunchesSubHeaderWord"> {word}</b>
                            {submittedQuery.trim() !== '' && (<>
                                &nbsp;and<b data-testid="textFoodLaunchesSubHeaderSearch"> {submittedQuery.toUpperCase()}</b>
                            </>)}
                            .
                        </div>
                    )}
                </div>
                {/* Locked: the page blurs the content (`is-locked`), so it is hidden from screen readers too. */}
                <div
                    className={cn("FoodSectionLaunches__content", isLocked && "is-locked")}
                    aria-hidden={isLocked || undefined}
                    data-testid="blockFoodSectionLaunchesContent"
                >
                    {!isLoading ? (<>
                        {gridData.length > 0 ? (<>
                            {selectedTab === LaunchesTabs.Items && (
                                <Grid
                                    // Locked shows the capped preview in full; limited access keeps the legacy one-row peek.
                                    isLimitedAccess={!isLocked && !hasFullAccess}
                                    data={gridData}
                                />
                            )}
                            {selectedTab === LaunchesTabs.Summary && (
                                <Summary data={summary}/>
                            )}
                        </>) : (
                            <div
                                className="FoodSectionLaunches__placeholder"
                                data-testid="textFoodLaunchesNoResultsPlaceholder"
                            >
                                No items found.
                            </div>
                        )}
                    </>) : (
                        <div className="spinner spinner-blink">
                            <ProductLogo appId={AppId.Snap} type="regular"/>
                        </div>
                    )}
                </div>
            </div>
            {!hasFullAccess && (
                <PermissionOverlay
                    onContactUsClick={() => requestTrial('scores')}
                    contentStyleModifiers={isLocked ? undefined : ['bottom']}
                    testId="blockFoodLaunchesPermissionOverlay"
                >
                    {isLocked ? lockedPreview.renderMessage(itemsCount) : (
                        <div data-testid="textFoodLaunchesPermissionOverlayContentLine-0">
                            Upgrade your subscription to reveal more new items from top chains.
                        </div>
                    )}
                </PermissionOverlay>
            )}
        </div>
    );
};

export default FoodSectionLaunches;